import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { orderTransferService } from '../../services/orderTransferService';
import { PICKING_CHANNELS, PickingChannel, resolveChannelLabel } from './pickingChannels';

/**
 * Kanal etiketleri + (opsiyonel) depo bazlı açık kanal filtresi.
 * warehouseId verilirse Akıllı Depo'da kaydedilen enabledChannels listesine
 * göre toplama/paketleme kartları filtrelenir; kapalı pazaryeriler gizlenir.
 */
export const useChannelLabel = (warehouseId?: string) => {
  const { companyName } = useAuth();
  /** null = filtre yok (tüm kanallar). dizi = yalnızca bu kaynaklar. */
  const [enabledSources, setEnabledSources] = useState<string[] | null>(null);

  useEffect(() => {
    if (!warehouseId) {
      setEnabledSources(null);
      return;
    }

    let cancelled = false;
    orderTransferService
      .getAutoTransferSettings(warehouseId)
      .then((settings) => {
        if (cancelled) return;
        // null/undefined = eski kayıt → tüm kanallar. Dizi (boş dahil) = kaydedilmiş seçim.
        const raw = settings?.enabledChannels;
        if (Array.isArray(raw)) {
          setEnabledSources(raw.map((c) => String(c).trim().toLowerCase()).filter(Boolean));
        } else {
          setEnabledSources(null);
        }
      })
      .catch(() => {
        if (!cancelled) setEnabledSources(null);
      });

    return () => {
      cancelled = true;
    };
  }, [warehouseId]);

  return useMemo(() => {
    const resolveLabel = (channel: Pick<PickingChannel, 'source' | 'label'>) =>
      resolveChannelLabel(channel, companyName);

    let channels: PickingChannel[] = PICKING_CHANNELS.map((c) => ({
      ...c,
      label: resolveLabel(c),
    }));

    if (enabledSources !== null) {
      const allow = new Set(enabledSources);
      channels = channels.filter((c) => allow.has(c.source));
    }

    return { resolveLabel, channels, enabledSources };
  }, [companyName, enabledSources]);
};

export default useChannelLabel;
